import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { CtaBanner } from '@/components/sections/CtaBanner';

export default function Guides() {
  const { t } = useTranslation();
  const guides = t('guides', { returnObjects: true });
  const [featured, ...rest] = guides;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Copy Trading Guides',
    url: 'https://copy.click/guides',
    description: t('guidesPage.seoDescription'),
    hasPart: guides.map((g) => ({
      '@type': 'Article',
      headline: g.title,
      url: `https://copy.click/guides/${g.slug}`,
      datePublished: g.date,
    })),
  };

  return (
    <>
      <Seo title={t('guidesPage.seoTitle')} description={t('guidesPage.seoDescription')} jsonLd={jsonLd} />

      <section className="relative overflow-hidden px-6 pb-24 pt-32 lg:px-8 sm:pt-40">
        <div className="pointer-events-none absolute right-0 top-24 -z-10 h-96 w-96 rounded-full bg-brand/10 blur-[130px]" />
        <div className="mx-auto max-w-6xl">
          <div className="text-center sm:text-left">
            <span className="eyebrow">{t('guidesPage.eyebrow')}</span>
            <h1 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.1] tracking-tight text-white sm:mx-0 sm:text-5xl">
              {t('guidesPage.title')}
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/60 sm:mx-0">
              {t('guidesPage.subtitle')}
            </p>
          </div>

          <Link to={`/guides/${featured.slug}`} data-testid="guide-featured" className="group mt-12 block rounded-3xl border border-brand/20 bg-[#001826]/70 p-8 transition-colors hover:border-brand/50 sm:p-10">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span className="rounded-full bg-brand/15 px-3 py-1 font-semibold text-brand">{featured.category}</span>
              <span className="flex items-center gap-1 text-white/40"><Clock className="h-3.5 w-3.5" /> {featured.readTime} {t('common.minRead')}</span>
            </div>
            <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold text-white sm:text-3xl">{featured.title}</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/55">{featured.description}</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand">{t('guidesPage.readGuide')} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
          </Link>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} data-testid="guide-card" className="group flex flex-col rounded-2xl border border-white/10 bg-surface/50 p-6 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-[0_20px_50px_rgba(23,253,253,0.08)]">
                <div className="flex items-center gap-3 text-xs">
                  <span className="rounded-full bg-brand/15 px-2.5 py-0.5 font-semibold text-brand">{g.category}</span>
                  <span className="flex items-center gap-1 text-white/40"><Clock className="h-3 w-3" /> {g.readTime} {t('common.min')}</span>
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold leading-snug text-white">{g.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-white/50">{g.description}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand">{t('common.read')} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
